import { FocusEvent, KeyboardEvent, useCallback } from 'react';

import KEYS from '@rover/constants/src/platform/keys.constants';
import useDebounce from '@rover/react-lib/src/hooks/useDebounce';

import { LOAD_OPTIONS_DEBOUNCE_TIME } from '../LocationInput.constants';
import type { SelectOption } from '../LocationInput.types';
import { shouldBlurElement } from '../utils';

type UseInputBodyActions = {
  handleInputChange: (newInputValue: string) => void;
  handleInputFocus: () => void;
  handleInputBlur: (e: FocusEvent<HTMLInputElement>) => void;
  handleKeydown: (e: KeyboardEvent<Element>) => void;
};

const useInputBodyActions = (
  inputId: string,
  options: SelectOption[],
  inputValue: string,
  countryCode: string | null | undefined,
  setActiveIndex: (index: number) => void,
  handleSelectChange: (option: SelectOption) => void,
  setInputValue: (value: string) => void,
  setInputFocused: (focused: boolean) => void,
  activeIndex?: number | null,
  onBlur?: () => void,
  onInputChange?: (value: string) => void,
  loadOptions?: (params: {
    query: string;
    inputId: string;
    countryCode: string | null | undefined;
  }) => void
): UseInputBodyActions => {
  const fetchOptions = useCallback(
    (newInputValue: string): void => {
      if (newInputValue && loadOptions) {
        loadOptions({
          query: newInputValue,
          inputId,
          countryCode,
        });
      }
    },
    [inputId, loadOptions, countryCode]
  );

  const { start: fetchOptionsDebounced, cancel: cancelFetchOptionsDebounced } = useDebounce(
    fetchOptions,
    LOAD_OPTIONS_DEBOUNCE_TIME
  );

  const updateInputValue = useCallback(
    (newInputValue: string): void => {
      fetchOptionsDebounced(newInputValue);
      setInputValue(newInputValue);
    },
    [fetchOptionsDebounced, setInputValue]
  );

  const handleInputChange = useCallback(
    (newInputValue: string): void => {
      updateInputValue(newInputValue);
      onInputChange?.(newInputValue);
    },
    [updateInputValue, onInputChange]
  );

  const handleInputFocus = useCallback((): void => {
    setInputFocused(true);
  }, [setInputFocused]);

  const handleInputBlur = useCallback(
    (e: FocusEvent<HTMLInputElement>): void => {
      if (shouldBlurElement(e)) {
        onBlur?.();
        setInputFocused(false);
      }
    },
    [onBlur, setInputFocused]
  );

  const handleKeydown = useCallback(
    (e: React.KeyboardEvent<Element>): void => {
      switch (e.key) {
        case KEYS.UP:
          e.preventDefault();
          setActiveIndex(
            typeof activeIndex === 'number' && activeIndex > 0
              ? activeIndex - 1
              : options.length - 1
          );
          break;
        case KEYS.DOWN:
          e.preventDefault();
          setActiveIndex(
            typeof activeIndex === 'number' && activeIndex < options.length - 1
              ? activeIndex + 1
              : 0
          );
          break;
        case KEYS.ENTER: {
          e.preventDefault();
          if (!inputValue) {
            return;
          }
          const selectedOption = typeof activeIndex === 'number' ? options[activeIndex] : undefined;
          // Once a user hits enter, stop the most recent debounce
          cancelFetchOptionsDebounced();
          handleSelectChange(
            selectedOption ?? {
              value: inputValue,
              label: inputValue,
            }
          );
          break;
        }
        default:
          break;
      }
    },
    [
      options,
      activeIndex,
      inputValue,
      setActiveIndex,
      cancelFetchOptionsDebounced,
      handleSelectChange,
    ]
  );

  return {
    handleInputChange,
    handleInputFocus,
    handleInputBlur,
    handleKeydown,
  };
};

export default useInputBodyActions;
